import React, { useMemo } from "react";
import DistributionHistogram from "../../components/patterns/DistributionHistogram";
import InfoIcon from "../../components/patterns/InfoIcon";
import TimelineChart from "../../components/patterns/charts/TimelineChart";
import CreatorConcentration from "../../components/patterns/charts/CreatorConcentration";
import DurationDistribution from "../../components/patterns/charts/DurationDistribution";
import OrbitalLoader from "../../components/common/OrbitalLoader";

const medianNumber = (values) => {
  if (!values?.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

const buildDistributionFromValues = (values, bucketCount = 10) => {
  if (!values?.length) return [];
  let min = Math.min(...values, -20);
  let max = Math.max(...values, 20);
  if (min === max) { min -= 10; max += 10; }
  const step = (max - min) / bucketCount;
  const buckets = Array.from({ length: bucketCount }, (_, i) => ({
    start: min + i * step,
    end: i === bucketCount - 1 ? max : min + (i + 1) * step,
    count: 0,
    open_ended: false,
  }));
  values.forEach((value) => {
    const idx = Math.min(bucketCount - 1, Math.max(0, Math.floor((value - min) / step)));
    buckets[idx].count += 1;
  });
  return buckets;
};

// For raw counts (views, likes, comments): use log-scale buckets when range is large
const buildDistributionFromCounts = (values, bucketCount = 8) => {
  if (!values?.length) return [];
  const filtered = values.filter((v) => v != null && v > 0);
  if (!filtered.length) return [];
  const min = Math.min(...filtered);
  const max = Math.max(...filtered);
  if (min === max) {
    return [{ start: min, end: max, count: filtered.length, open_ended: false }];
  }
  const logMin = Math.log10(Math.max(1, min));
  const logMax = Math.log10(Math.max(1, max));
  const step = (logMax - logMin) / bucketCount;
  const buckets = Array.from({ length: bucketCount }, (_, i) => {
    const s = Math.pow(10, logMin + i * step);
    const e = i === bucketCount - 1 ? max : Math.pow(10, logMin + (i + 1) * step);
    return { start: Math.round(s), end: Math.round(e), count: 0, open_ended: i === bucketCount - 1 };
  });
  filtered.forEach((value) => {
    const logV = Math.log10(Math.max(1, value));
    const idx = Math.min(bucketCount - 1, Math.max(0, Math.floor((logV - logMin) / (step || 1))));
    buckets[idx].count += 1;
  });
  return buckets;
};

const STAT_ITEMS = [
  {
    key: "likes",
    label: "Likes",
    valuePath: (p) => p?.stats?.likes,
    infoTitle: "Median likes per video in this pattern.",
  },
  {
    key: "comments",
    label: "Comments",
    valuePath: (p) => p?.stats?.comments,
    infoTitle: "Median comments per video in this pattern.",
  },
  {
    key: "views",
    label: "Views",
    valuePath: (p) => p?.stats?.views,
    infoTitle: "Median views per video in this pattern.",
  },
  {
    key: "engagement",
    label: "Engagement",
    valuePath: (p) => p?.stats?.engagement_rate,
    formatValue: (v) => `${Number(v).toFixed(2)}%`,
    infoTitle: "Median engagement rate: (likes + comments) / views.",
  },
];

const PatternStats = ({ pattern, baseline, formatNumber, patternVideoRows, loadingVideos }) => {
  const formatNum = formatNumber ?? ((v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v)));
  const outlierToPct = (outlier) => (outlier != null && !Number.isNaN(outlier) ? (Number(outlier) - 1) * 100 : null);

  const rawCountDistributionData = useMemo(() => {
    const views = patternVideoRows.map((r) => r.video?.count_views).filter((v) => v != null && v > 0);
    const likes = patternVideoRows.map((r) => r.video?.count_likes).filter((v) => v != null && v > 0);
    const comments = patternVideoRows.map((r) => r.video?.count_comments).filter((v) => v != null && v >= 0);
    return [
      { key: "views", label: "Views", values: views, distribution: buildDistributionFromCounts(views), median: medianNumber(views) },
      { key: "likes", label: "Likes", values: likes, distribution: buildDistributionFromCounts(likes), median: medianNumber(likes) },
      { key: "comments", label: "Comments", values: comments, distribution: buildDistributionFromCounts(comments), median: medianNumber(comments) },
    ];
  }, [patternVideoRows]);

  const outlierDistributionData = useMemo(() => {
    return [
      { key: "likes", label: "Likes outlier %", values: patternVideoRows.map((r) => outlierToPct(r.creator_likes_outlier)).filter((v) => v != null && !Number.isNaN(v)) },
      { key: "comments", label: "Comments outlier %", values: patternVideoRows.map((r) => outlierToPct(r.creator_comments_outlier)).filter((v) => v != null && !Number.isNaN(v)) },
      { key: "views", label: "Views outlier %", values: patternVideoRows.map((r) => outlierToPct(r.creator_views_outlier)).filter((v) => v != null && !Number.isNaN(v)) },
    ].map((metric) => ({
      ...metric,
      distribution: buildDistributionFromValues(metric.values),
      median: medianNumber(metric.values),
    }));
  }, [patternVideoRows]);

  if (loadingVideos && patternVideoRows.length === 0) {
    return (
      <div className="flex justify-center py-10 text-theme-text">
        <OrbitalLoader size={62} text="Loading stats..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div>
        <h4 className="text-sm font-semibold text-theme-text mb-2">Stats</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {STAT_ITEMS.map((item) => {
            const rawValue = item.valuePath(pattern);
            const displayValue = rawValue != null
              ? (item.formatValue ? item.formatValue(rawValue) : formatNumber(rawValue))
              : "N/A";
            return (
              <div key={item.key} className="bg-theme-secondary rounded-lg p-3 border border-theme-secondary">
                <div className="flex items-center gap-1.5 text-xs text-theme-text opacity-60 uppercase tracking-wider">
                  {item.label}
                  <InfoIcon title={item.infoTitle} />
                </div>
                <span className="text-xl font-semibold text-theme-text">{displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-theme-secondary rounded-lg p-4 border border-theme-secondary">
        <h4 className="text-sm font-semibold text-theme-text mb-2">Timeline trend</h4>
        <TimelineChart videoRows={patternVideoRows} loading={loadingVideos} />
      </div>

      {/* Views, Likes, Comments distribution */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-theme-text">
          Views, Likes, Comments distribution
          <span className="ml-1 font-normal text-xs opacity-60">(raw counts per video)</span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {rawCountDistributionData.map((item) => (
            <div key={item.key} className="bg-theme-secondary rounded-lg p-4 border border-theme-secondary">
              {item.distribution?.length ? (
                <DistributionHistogram
                  distribution={item.distribution}
                  patternMedian={item.median}
                  baselineMedian={baseline?.[item.key]?.p50}
                  label={item.label}
                  formatValue={formatNum}
                />
              ) : (
                <div className="flex items-center justify-center h-16 text-theme-text opacity-50 text-sm">
                  No {item.label.toLowerCase()} data
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Outlier distributions */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-theme-text">
          Outlier vs Creator
          <span className="ml-1 font-normal text-xs opacity-60">(how each video compares to its creator's median)</span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {outlierDistributionData.map((item) => (
            <div key={item.key} className="bg-theme-secondary rounded-lg p-4 border border-theme-secondary">
              {item.distribution?.length ? (
                <DistributionHistogram
                  distribution={item.distribution}
                  patternMedian={item.median}
                  baselineMedian={0}
                  label={item.label}
                  formatValue={(v) => `${Number(v).toFixed(0)}%`}
                />
              ) : (
                <div className="flex items-center justify-center h-16 text-theme-text opacity-50 text-sm">
                  No outlier data
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Creator concentration & duration */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-theme-secondary rounded-lg p-4 border border-theme-secondary">
          <CreatorConcentration videoRows={patternVideoRows} />
        </div>
        <div className="bg-theme-secondary rounded-lg p-4 border border-theme-secondary">
          <DurationDistribution videoRows={patternVideoRows} />
        </div>
      </div>
    </div>
  );
};

export default PatternStats;
